/**
 * Cart store (frontend-only) — "Récapitulatif de commande".
 *
 * Pas de checkout, pas de paiement : c'est une checklist persistée que le
 * client garde sous les yeux pour passer commande par téléphone.
 *
 * Persistance : localStorage (clé `le11:cart:v1`).
 *
 * Schéma d'une ligne :
 *   {
 *     id:         string  -- identifiant unique de ligne
 *     kind:       "pizza" | "menu" | "item"
 *     name:       string  -- libellé court principal
 *     details:    string[] -- bullets affichés sous le libellé (base, viandes…)
 *     unitPrice:  number  -- prix unitaire estimé (€)
 *     qty:        number  -- quantité
 *   }
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "le11:cart:v1";

const safeRead = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWrite = (items) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota */
  }
};

const genId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => safeRead());
  const [lastAdd, setLastAdd] = useState(null); // { id, name, ts }
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    safeWrite(items);
  }, [items]);

  const addItem = useCallback((item) => {
    const line = {
      id: genId(),
      kind: item.kind || "item",
      name: item.name,
      details: item.details || [],
      unitPrice: Number(item.unitPrice) || 0,
      qty: Math.max(1, Number(item.qty) || 1),
    };
    setItems((prev) => [...prev, line]);
    setLastAdd({ id: line.id, name: line.name, ts: Date.now() });
    return line.id;
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const updateQty = useCallback((id, qty) => {
    const safeQty = Math.max(1, Math.min(20, Number(qty) || 1));
    setItems((prev) =>
      prev.map((l) => (l.id === id ? { ...l, qty: safeQty } : l))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const total = useMemo(
    () => items.reduce((s, l) => s + l.unitPrice * l.qty, 0),
    [items]
  );

  const count = useMemo(
    () => items.reduce((s, l) => s + l.qty, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      updateQty,
      clear,
      total,
      count,
      lastAdd,
      drawerOpen,
      openDrawer,
      closeDrawer,
    }),
    [
      items,
      addItem,
      removeItem,
      updateQty,
      clear,
      total,
      count,
      lastAdd,
      drawerOpen,
      openDrawer,
      closeDrawer,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart() must be used inside <CartProvider>");
  }
  return ctx;
};

export const formatEUR = (n) => `${(Number(n) || 0).toFixed(2).replace(".", ",")} €`;

export const parseEUR = (str) => {
  if (typeof str === "number") return str;
  if (typeof str !== "string") return 0;
  return parseFloat(str.replace("€", "").replace(",", ".").trim()) || 0;
};
